import React from 'react';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { formatDate } from './Shared';

// Public-facing data honesty badges. Any figure that is not verified must be
// clearly labeled so residents never mistake sample numbers for official facts.
export const REVIEW_LABELS = {
  sample: 'Sample data',
  draft: 'Draft',
  needs_review: 'Needs review',
  source_based: 'Source-based',
  source_needed: 'Source needed',
  verified: 'Verified',
};

export const REVIEW_STYLES = {
  sample: 'bg-amber-50 text-amber-900 border-amber-300',
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  needs_review: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
  source_based: 'bg-sky-50 text-sky-900 border-sky-300',
  source_needed: 'bg-amber-50 text-amber-900 border-amber-300',
  verified: 'bg-emerald-50 text-emerald-900 border-emerald-300',
};

export const ReviewBadge = ({ status }) => {
  if (!status || !REVIEW_LABELS[status]) return null;
  return (
    <Badge variant="outline" className={`${REVIEW_STYLES[status]} font-semibold text-[11px] uppercase tracking-wide`} data-testid={`review-badge-${status}`}>
      {REVIEW_LABELS[status]}
    </Badge>
  );
};

export const SourceNote = ({ sourceName, sourceUrl, sourceNote, lastUpdated, testId = 'source-note' }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pt-3 mt-3 border-t border-slate-100 min-w-0 max-w-full" data-testid={testId}>
    {sourceUrl ? (
      <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4 inline-flex items-center gap-1 min-w-0 max-w-full break-words [overflow-wrap:anywhere]">
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">Source: {sourceName || 'View source'}</span> <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
    ) : sourceName ? (
      <span className="text-xs text-slate-500 min-w-0 max-w-full break-words [overflow-wrap:anywhere]">Source: {sourceName}</span>
    ) : (
      <span className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1" data-testid="data-source-needed"><AlertCircle className="h-3 w-3" /> Data source needed</span>
    )}
    {lastUpdated && <span className="text-[11px] text-slate-400">Updated {formatDate(lastUpdated)}</span>}
    {sourceNote && <p className="w-full text-[11px] text-slate-500 leading-relaxed break-words [overflow-wrap:anywhere]">{sourceNote}</p>}
  </div>
);

export const SampleDataBanner = ({ text, testId = 'sample-data-banner' }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-testid={testId}>
    <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
    <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
      {text || 'Some figures on this page are sample placeholders and are clearly labeled. They are NOT verified City of Kawartha Lakes numbers. Verified data will replace them as sources are confirmed, and every chart shows its source or "Data source needed."'}
    </p>
  </div>
);
